/**
 * Custom Hook for Menu Management
 * Provides menu permissions and filtering functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MenuItem, MenuPermission } from '../types/menu';
import { ALL_MENU_ITEMS } from '../config/menuConfig';
import { filterMenusByPermissions, debugMenuPermissions } from '../utils/menuPermissions';
import { RootState } from '../store/index';
import { updateUser } from '../store/slices/authSlice';

/** Flatten nested permission tree so sidebar can match by menu id (e.g. invoice under billing becomes top-level). */
function flattenPermissionTree(perms: Record<string, MenuPermission>): Record<string, MenuPermission> {
	const flat: Record<string, MenuPermission> = {};
	function walk(p: Record<string, MenuPermission>) {
		Object.entries(p).forEach(([id, perm]) => {
			if (perm && typeof perm === 'object' && 'access' in perm) {
				flat[id] = perm;
				if (perm.children && Object.keys(perm.children).length > 0) {
					walk(perm.children);
				}
			}
		});
	}
	walk(perms);
	return flat;
}

export interface UseUserMenusReturn {
	// Menu data
	menus: MenuItem[];
	permissions: Record<string, MenuPermission>;
	accessibleMenuIds: string[];
	userType: string;
	userTypeId: number;

	// Loading states
	loading: boolean;
	error: string | null;

	// Actions
	refreshPermissions: (newPermissions?: Record<string, MenuPermission>) => Promise<void>;
	checkMenuAccess: (menuId: string) => boolean;

	// Debug utilities
	debugPermissions: () => void;
}

export const useUserMenus = (): UseUserMenusReturn => {
	const dispatch = useDispatch();
	const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

	const [accessibleMenuIds, setAccessibleMenuIds] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	// Get permissions from Redux state
	const permissions = user?.menuPermissions || {};

	// Flatten so nested menus (e.g. invoice under billing) are visible at top level for sidebar match
	const flatPermissions = flattenPermissionTree(permissions);

	// Normalize: API uses "invoice-menu" (and possibly invoices, billing, etc.); config uses "invoice"
	const normalizedPermissions = { ...flatPermissions };
	let invoicePerm: MenuPermission | undefined;
	if (flatPermissions['invoice-menu']) {
		invoicePerm = flatPermissions['invoice-menu'];
		normalizedPermissions['invoice'] = invoicePerm;
	}
	const invoiceLikeKeys = Object.keys(flatPermissions).filter(k => /invoice|billing/i.test(k));
	if (invoiceLikeKeys.length > 0 && !normalizedPermissions['invoice']) {
		const withAccess = invoiceLikeKeys.find(k => flatPermissions[k]?.access === true);
		invoicePerm = withAccess
			? flatPermissions[withAccess]
			: flatPermissions[invoiceLikeKeys[0]];
		normalizedPermissions['invoice'] = invoicePerm;
	}
	// Normalize invoice permission children so backend slugs match config ids (invoice-list, billing-details)
	if (invoicePerm?.children && typeof invoicePerm.children === 'object') {
		const ch = invoicePerm.children as Record<string, MenuPermission>;
		const normalizedChildren = { ...ch };
		if (ch['invoice-listing'] && !normalizedChildren['invoice-list']) {
			normalizedChildren['invoice-list'] = ch['invoice-listing'];
		}
		if (ch['invoice-list'] && !normalizedChildren['invoice-list']) {
			normalizedChildren['invoice-list'] = ch['invoice-list'];
		}
		if (ch['billing'] && !normalizedChildren['billing-details']) {
			normalizedChildren['billing-details'] = ch['billing'];
		}
		if (ch['billing-details'] && !normalizedChildren['billing-details']) {
			normalizedChildren['billing-details'] = ch['billing-details'];
		}
		normalizedPermissions['invoice'] = {
			...invoicePerm,
			children: Object.keys(normalizedChildren).length > 0 ? normalizedChildren : invoicePerm.children,
		};
	}

	// Billing (Pulse, Clients, Follow-Up Tracker, Customer Intelligence) and Finances
	// (Zoho AR/AP + Amazon Invoice) are permission-gated only — no force-grant.
	// Seeded 2026-09-08: menus.slug pulse / clients-ledger / follow-up-tracker /
	// customer-intelligence now exist (ids 132-135 under invoice-menu 47) and are
	// granted to City Head / accounts / Founders. The five legacy billing ids
	// (client-health, overdue-behaviour, pipeline-gaps, broken-commitments,
	// ceo-overview) are commented out in menuConfig, so they need no rows.


	// Update accessible menu IDs when permissions change
	useEffect(() => {
		if (permissions && Object.keys(permissions).length > 0) {
			const accessibleIds = Object.keys(permissions).filter(id => permissions[id]?.access);
			setAccessibleMenuIds(accessibleIds);
			setLoading(false);
			setError(null);
		} else if (isAuthenticated) {
			setError('No menu permissions found. Please log in again.');
			setLoading(false);
		}
	}, [permissions, isAuthenticated]);

	// The Approvals menu is gated by a per-user feature flag (isApprover /
	// proxyEligible from /procurement/me/is-approver), NOT by the role-based
	// menu_permissions table — because not every "accounts" user is an
	// approver. Only the specific individuals designated as approvers
	// (Shashwat / Asha / Swati / Priyanka) should see it.
	//
	// Problem this solves: a finance user like Asha has user_type='accounts',
	// which does not have menu_permissions rows for 'approvals' or
	// 'vendor-invoice-approvals'. filterMenusByPermissions strips the whole
	// subtree. The old patch (below) only prevented *removing* the parent;
	// the child submenu was already gone by then. Result: Asha saw the
	// Approvals parent but no clickable submenu.
	//
	// Fix: before filtering, if the user is an approver or proxy-eligible,
	// inject a fully-granted approvals subtree into their permission map.
	// filterMenusByPermissions then preserves both parent and children
	// exactly as designed. Non-approvers still fall through with no change,
	// so blanket-granting to every accounts user is avoided.
	if (user?.isApprover || user?.proxyEligible) {
		// Preserve whatever approvals children the server already granted (the
		// backend now seeds the email-payment IDs) and ensure the approver-only
		// vendor-invoice child is present. Replacing the whole children map here
		// would drop the server-granted email-workflow items for approvers.
		const serverApprovals = normalizedPermissions['approvals'];
		const serverApprovalsChildren =
			(serverApprovals?.children as Record<string, MenuPermission> | undefined) || {};
		normalizedPermissions['approvals'] = {
			access: true,
			children: {
				...serverApprovalsChildren,
				'vendor-invoice-approvals': { access: true, children: {} },
			},
		};
	}

	// Task Board is permission-gated only. Seeded 2026-09-08 as menus.slug 'task-board'
	// (id 136, root) and granted to City Head / Founders.

	// Recruitment (ATS) is permission-gated only. Verified 2026-09-08: hr-recruitment,
	// job-posting and the four ATS leaves all exist in `menus` and are already granted
	// to City Head / Founders / HR. The old force-grant leaked HR to every user.

	// Payroll shows salaries, so it is permission-gated: only user types granted the 'payroll' menu see it.
	// A grant on 'payroll' alone opens both pages, so one menu_permissions row per user type is enough.
	const payrollPerm = flatPermissions['payroll'];
	if (payrollPerm?.access === true && !Object.keys(payrollPerm.children || {}).length) {
		normalizedPermissions['payroll'] = {
			access: true,
			children: {
				'payroll-overview': { access: true, children: {} },
				'payroll-entry': { access: true, children: {} },
			},
		};
	}

	let menus = filterMenusByPermissions(ALL_MENU_ITEMS, normalizedPermissions);

	// Check if user has access to a specific menu
	const checkMenuAccess = useCallback(
		(menuId: string): boolean => {
			return permissions[menuId]?.access === true;
		},
		[permissions]
	);

	// Refresh permissions (useful after role changes)
	const refreshPermissions = useCallback(
		async (newPermissions?: Record<string, MenuPermission>) => {
			try {
				if (newPermissions) {
					// Update Redux state with new permissions
					dispatch(updateUser({ menuPermissions: newPermissions }));
				}
			} catch (error) {
				console.error('Failed to refresh menu permissions:', error);
			}
		},
		[dispatch]
	);

	// Debug utility
	const debugPermissions = useCallback(() => {
		debugMenuPermissions(permissions);
	}, [permissions]);

	return {
		menus,
		permissions,
		accessibleMenuIds,
		userType: user?.role || '', // From Redux store
		userTypeId: user?.userTypeId || 0, // From Redux store
		loading,
		error,
		refreshPermissions,
		checkMenuAccess,
		debugPermissions,
	};
};
